/**
 * What the AI provider behind this deployment can do, as far as the browser
 * can tell.
 *
 * Every AI feature posts one wire format to the AI endpoint and the SERVER
 * picks the provider (web/api/ai/_provider.js). Two things differ by provider
 * and matter to a caller:
 *
 *   - provider-hosted tools (live web search) exist on Anthropic only. On any
 *     other provider the server removes the tool from the request and says so
 *     in the `x-ai-dropped` response header.
 *   - which provider answered, in the `x-ai-provider` response header.
 *
 * "An AI endpoint is configured" and "that endpoint can search the web" are
 * separate questions. `getAiProvider().name === 'claude'` (an internal name)
 * answers only the first.
 *
 * PURE except for one remembered value (the provider last seen on a response),
 * which exists so a feature does not have to spend a model call to find out
 * what the previous one already learned.
 */

export type AiProviderName = 'anthropic' | 'azure-openai' | 'openai'

const KNOWN: readonly AiProviderName[] = ['anthropic', 'azure-openai', 'openai']

/** A provider name from a header or the status route, or null when unknown. */
export function normalizeProviderName(raw: unknown): AiProviderName | null {
  const v = typeof raw === 'string' ? raw.trim().toLowerCase() : ''
  return (KNOWN as readonly string[]).includes(v) ? (v as AiProviderName) : null
}

export interface AiResponseMeta {
  /** The provider that answered, or null when the endpoint did not say. */
  provider: AiProviderName | null
  /** Request features the provider could not honor and the server left out. */
  dropped: string[]
}

interface HeaderReader {
  get(name: string): string | null
}

/** Read the provider headers off an AI response. Never throws. */
export function readAiResponseMeta(headers: HeaderReader | null | undefined): AiResponseMeta {
  let provider: string | null = null
  let dropped: string | null = null
  try {
    provider = headers?.get('x-ai-provider') ?? null
    dropped = headers?.get('x-ai-dropped') ?? null
  } catch {
    /* a response without readable headers says nothing */
  }
  return {
    provider: normalizeProviderName(provider),
    dropped: (dropped ?? '')
      .split(',')
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean),
  }
}

/** True when the server removed the web search tool from the request. */
export function webSearchWasDropped(meta: AiResponseMeta): boolean {
  return meta.dropped.some((d) => d === 'web_search' || d.startsWith('web_search'))
}

export type Capability = 'available' | 'unavailable' | 'unknown'

/**
 * Whether live web search can run on a provider.
 *
 * `unknown` (the endpoint named no provider: an older proxy, the S/4-hosted
 * relay, a custom AI link) is not treated as "no": the request is sent with
 * the tool and the response decides.
 */
export function liveWebSearchOn(provider: AiProviderName | null | undefined): Capability {
  if (!provider) return 'unknown'
  return provider === 'anthropic' ? 'available' : 'unavailable'
}

/**
 * Whether a caller may use Anthropic-only request variants (for example
 * retrying with an older hosted tool version). False as soon as the endpoint
 * is known to be another provider.
 */
export function mayUseAnthropicOnlyFeatures(provider: AiProviderName | null | undefined): boolean {
  return !provider || provider === 'anthropic'
}

// ─── The provider last seen on a response ─────────────────────────────────────

let observed: AiProviderName | null = null

/** Remember what an AI response said about its provider. */
export function noteAiResponseMeta(meta: AiResponseMeta): void {
  if (meta.provider) observed = meta.provider
}

/** The provider last seen on an AI response in this session, or null. */
export function observedAiProvider(): AiProviderName | null {
  return observed
}

/** Test seam: forget the observed provider. */
export function resetAiCapabilitiesForTests(): void {
  observed = null
}
